import type { ReactElement, ReactNode } from 'react';

interface PageHeaderProps {
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  readonly icon?: ReactNode;
  /** Right-aligned actions (Export, filters, primary CTA, etc.). */
  readonly actions?: ReactNode;
}

/**
 * V1 page-header anatomy used by every non-record route. Title +
 * optional subtitle on the left, icon ahead of the title, optional
 * action cluster flush right. Identity-band styling for parity with
 * the patient record's case header.
 */
export function PageHeader({
  title,
  subtitle,
  icon,
  actions,
}: PageHeaderProps): ReactElement {
  return (
    <header className="border-b border-border bg-card px-8 py-3 flex items-center justify-between gap-6">
      <div className="min-w-0 flex-1">
        <h1 className="text-[15px] font-semibold flex items-center gap-2 leading-tight tracking-tight">
          {icon !== undefined && (
            <span className="text-muted-foreground" aria-hidden="true">
              {icon}
            </span>
          )}
          <span className="truncate">{title}</span>
        </h1>
        {subtitle !== undefined && subtitle !== null && (
          <p className="text-[12px] text-muted-foreground mt-1">{subtitle}</p>
        )}
      </div>
      {actions !== undefined && actions !== null && (
        <div className="flex items-center gap-2 shrink-0">{actions}</div>
      )}
    </header>
  );
}
